import {
  type Completion,
  type CompletionContext,
  type CompletionResult,
  type CompletionSource,
} from "@codemirror/autocomplete";
import { type Command, type EditorView } from "@codemirror/view";
import { insertTable } from "./tableWidget.ts";
import { linkCommand, type LinkUIOptions } from "./linkUI.ts";

/**
 * Slash menu (Fase 3): type `/` at the start of a line to open a block picker,
 * Notion-style. Built on CodeMirror's autocomplete so we get the popup,
 * keyboard navigation (↑/↓, Enter, Esc) and click-to-pick for free. El popup
 * en sí lo arma `completions.ts`, que junta esta fuente con la de `[[`: dos
 * `autocompletion()` sobre el mismo editor se pisan.
 *
 * Each command just rewrites the line into the plain markdown a person would
 * have typed (`# `, `- [ ] `, `> `…), so the `.md` stays portable — same
 * guarantee as Fases 1 & 2.
 */

interface BlockCommand {
  /** Pretty name shown in the menu. */
  label: string;
  /** Short markdown hint shown on the right. */
  detail: string;
  /** Icon bucket (drives the .cm-completionIcon-<type> glyph). */
  type: string;
  /** Words that match this command when filtering (lowercase, no accents). */
  keywords: string[];
  /** Text inserted in place of the `/query`. */
  insert: string;
  /** Caret offset within `insert` (defaults to end). */
  caret?: number;
  /**
   * Comando a ejecutar en lugar de insertar texto (para lo que no es un simple
   * prefijo de bloque). Se corre después de borrar el `/query`, así reusa tal
   * cual el comando del atajo de teclado y los dos caminos no se separan.
   */
  run?: Command;
  /**
   * El comando se arma con las opciones del documento (rutas del vault, ruta
   * actual). Es lo que necesita el enlace: su UI ofrece los destinos del vault
   * y escribe la ruta relativa correcta.
   */
  ui?: boolean;
}

const COMMANDS: BlockCommand[] = [
  { label: "Heading 1", detail: "# ", type: "heading", keywords: ["heading", "h1", "title"], insert: "# " },
  { label: "Heading 2", detail: "## ", type: "heading", keywords: ["heading", "h2", "title"], insert: "## " },
  { label: "Heading 3", detail: "### ", type: "heading", keywords: ["heading", "h3", "title"], insert: "### " },
  { label: "List", detail: "- ", type: "list", keywords: ["list", "bullet", "point", "unordered"], insert: "- " },
  { label: "Numbered list", detail: "1. ", type: "list", keywords: ["list", "numbered", "ordered", "number"], insert: "1. " },
  { label: "Task", detail: "- [ ] ", type: "task", keywords: ["task", "todo", "checkbox", "check"], insert: "- [ ] " },
  { label: "Link", detail: "text + destination", type: "link", keywords: ["link", "enlace", "url", "hipervinculo", "href"], insert: "", ui: true },
  { label: "Quote", detail: "> ", type: "quote", keywords: ["quote", "blockquote", "citation"], insert: "> " },
  { label: "Code", detail: "``` ```", type: "code", keywords: ["code", "block", "snippet"], insert: "```\n\n```", caret: 4 },
  { label: "Table", detail: "3 × 2", type: "table", keywords: ["table", "tabla", "grid", "cuadro", "columns", "columnas"], insert: "", run: insertTable },
  { label: "Divider", detail: "———", type: "divider", keywords: ["divider", "separator", "line", "hr", "rule"], insert: "---\n" },
];

/** Strip accents so "código" matches a typed "codigo". */
function normalize(s: string): string {
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

function toCompletion(cmd: BlockCommand, opts: LinkUIOptions): Completion {
  return {
    label: cmd.label,
    detail: cmd.detail,
    type: cmd.type,
    apply: (view: EditorView, _c: Completion, from: number, to: number) => {
      const caret = from + (cmd.caret ?? cmd.insert.length);
      view.dispatch({
        changes: { from, to, insert: cmd.insert },
        selection: { anchor: caret },
        scrollIntoView: true,
        userEvent: "input.complete",
      });
      if (cmd.ui) linkCommand(opts)(view);
      else cmd.run?.(view);
    },
  };
}

export function slashSource(opts: LinkUIOptions): CompletionSource {
  return (context: CompletionContext): CompletionResult | null => {
  const line = context.state.doc.lineAt(context.pos);
  const before = line.text.slice(0, context.pos - line.from);
  // Only when the line so far is just whitespace + "/" + a single-word query.
  const match = /^\s*\/([\p{L}\p{N}]*)$/u.exec(before);
  if (!match) return null;

  const query = normalize(match[1]);
  const options = COMMANDS.filter(
    (c) => !query || c.keywords.some((k) => k.includes(query)),
  ).map((c) => toCompletion(c, opts));
  if (!options.length) return null;

  return {
    from: context.pos - match[1].length - 1, // the "/" position
    to: context.pos,
    options,
    filter: false, // we filter ourselves (with accent folding + keywords)
  };
  };
}
